import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaTimes, FaFilter, FaMapMarkerAlt, FaStar, FaLocationArrow, FaPlus, FaMinus } from 'react-icons/fa';
import { http } from '../lib/http';
import { useI18n } from '../contexts/I18n';
import { createImageHandler } from '../utils/imageHandler';
import ELogo from './ELogo';
import './Map.css';

// Service categories (matching Dashboard)
const serviceCategories = [
  { title: 'All Categories', serviceCategory: null, titleAr: 'جميع الفئات' },
  { title: 'Barber', serviceCategory: 'Barber', titleAr: 'حلاق' },
  { title: 'Hair & Nail', serviceCategory: 'Hair & Nail', titleAr: 'صالون شعر وأظافر' },
  { title: 'Brows & lashes', serviceCategory: 'Brows & lashes', titleAr: 'حواجب ورموش' },
  { title: 'Laser Clinics', serviceCategory: 'Laser Clinics', titleAr: 'عيادات الليزر' },
  { title: 'Makeup', serviceCategory: 'Makeup', titleAr: 'مكياج' },
  { title: 'Massage', serviceCategory: 'Massage', titleAr: 'مساج' },
  { title: 'Tatoo and Piercing', serviceCategory: 'Tatoo and Piercing', titleAr: 'وشم وثقب' },
  { title: 'Pet Services', serviceCategory: 'Pet Services', titleAr: 'خدمات الحيوانات الأليفة' },
];

export default function Map() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [shops, setShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);
  const [shopImageIndex, setShopImageIndex] = useState(0);
  const galleryRef = useRef(null);
  const [userLocation, setUserLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 30.0444, lng: 31.2357 });
  const [mapZoom, setMapZoom] = useState(12);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const imageHandler = useMemo(() => createImageHandler('shop'), []);

  // Validate coordinates are within Egypt
  const isValidEgyptCoordinates = (lat, lng) => {
    return lat >= 22 && lat <= 32 && lng >= 25 && lng <= 37;
  };

  // Parse Google Maps URL/embed for lat,lng (same source as booking page map link / embed)
  const parseCoordsFromMapEmbed = (str) => {
    if (!str || typeof str !== 'string') return null;
    const s = str.trim();
    // @lat,lng or @lat,lng,zoom (e.g. .../place/Name/@30.0444,31.2357,17z)
    const atMatch = s.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)(?:,|\/|z|$)/);
    if (atMatch) {
      const lat = parseFloat(atMatch[1]);
      const lng = parseFloat(atMatch[2]);
      if (Number.isFinite(lat) && Number.isFinite(lng)) return { latitude: lat, longitude: lng };
    }
    // !3dLAT!4dLNG (e.g. ...!3d30.0444!4d31.2357!16z)
    const dMatch = s.match(/!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/);
    if (dMatch) {
      const lat = parseFloat(dMatch[1]);
      const lng = parseFloat(dMatch[2]);
      if (Number.isFinite(lat) && Number.isFinite(lng)) return { latitude: lat, longitude: lng };
    }
    // ll=lat,lng
    const llMatch = s.match(/[?&]ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (llMatch) {
      const lat = parseFloat(llMatch[1]);
      const lng = parseFloat(llMatch[2]);
      if (Number.isFinite(lat) && Number.isFinite(lng)) return { latitude: lat, longitude: lng };
    }
    // q=lat,lng
    const qMatch = s.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (qMatch) {
      const lat = parseFloat(qMatch[1]);
      const lng = parseFloat(qMatch[2]);
      if (Number.isFinite(lat) && Number.isFinite(lng)) return { latitude: lat, longitude: lng };
    }
    return null;
  };

  // Get shop coordinates: prefer mapEmbed (same URL as View Map on booking page) so pin matches where View Map opens
  const getShopCoordinates = (shop) => {
    // 1) mapEmbed: same URL as "View Map" on booking page – use first so map pin matches View Map
    const fromEmbed = parseCoordsFromMapEmbed(shop.mapEmbed);
    if (fromEmbed && isValidEgyptCoordinates(fromEmbed.latitude, fromEmbed.longitude)) {
      return fromEmbed;
    }

    // 2) GeoJSON location.coordinates [longitude, latitude]
    if (shop.location && shop.location.coordinates && Array.isArray(shop.location.coordinates) && shop.location.coordinates.length >= 2) {
      const lng = parseFloat(shop.location.coordinates[0]);
      const lat = parseFloat(shop.location.coordinates[1]);
      if (Number.isFinite(lat) && Number.isFinite(lng) && isValidEgyptCoordinates(lat, lng)) {
        return { latitude: lat, longitude: lng };
      }
    }

    // 3) Explicit latitude/longitude (or lat/lng) – use only if valid Egypt
    const lat = parseFloat(shop.latitude ?? shop.lat);
    const lng = parseFloat(shop.longitude ?? shop.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng) && isValidEgyptCoordinates(lat, lng)) {
      return { latitude: lat, longitude: lng };
    }

    // 4) Fallback: city/town center (no offset – use exact center so it matches “town” used on booking page)
    const cityCoordinates = {
      'Cairo': { latitude: 30.0444, longitude: 31.2357 },
      'New Cairo': { latitude: 30.0131, longitude: 31.4046 },
      'Heliopolis': { latitude: 30.0886, longitude: 31.3197 },
      'Masr El Gedida': { latitude: 30.0886, longitude: 31.3197 },
      'October City': { latitude: 29.9697, longitude: 30.9564 },
      'Alexandria': { latitude: 31.2001, longitude: 29.9187 },
      'Giza': { latitude: 30.0131, longitude: 31.2089 },
    };

    const town = (shop.town || shop.city || '').trim();
    return cityCoordinates[town] ||
      cityCoordinates[Object.keys(cityCoordinates).find(k => k.toLowerCase() === town.toLowerCase())] ||
      cityCoordinates['Cairo'];
  };

  // Calculate distance between two coordinates
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of the Earth in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in km
  };

  // Fetch shops
  useEffect(() => {
    const fetchShops = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await http.getJson('/businesses?limit=1000&page=1');
        const shopsData = Array.isArray(data) ? data : [];
        
        const shopsWithCoords = shopsData
          .filter(shop => shop && shop.name)
          .map(shop => {
            let coords = getShopCoordinates(shop);
            if (!coords || !Number.isFinite(coords.latitude) || !Number.isFinite(coords.longitude)) {
              coords = { latitude: 30.0444, longitude: 31.2357 };
            } else if (!isValidEgyptCoordinates(coords.latitude, coords.longitude)) {
              coords = { latitude: 30.0444, longitude: 31.2357 };
            }
            return {
              ...shop,
              coordinates: coords,
            };
          });
        
        setShops(shopsWithCoords);
        setLoading(false);
      } catch (err) {
        console.error('Error loading shops:', err);
        setError(err.message || 'Failed to load shops. Please try again.');
        setShops([]);
        setLoading(false);
      }
    };

    fetchShops();
  }, []);

  // Get user location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          if (isValidEgyptCoordinates(latitude, longitude)) {
            setUserLocation({ latitude, longitude });
          }
        },
        (err) => {
          console.log('Location error:', err);
        }
      );
    }
  }, []);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || loading) return;

    // Load Leaflet CSS and JS
    if (!window.L) {
      // Check if CSS is already loaded
      if (!document.querySelector('link[href*="leaflet"]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      }

      // Check if script is already loading
      if (!document.querySelector('script[src*="leaflet"]')) {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => {
          initializeMap();
        };
        document.body.appendChild(script);
      } else {
        // Script is loading, wait for it
        const checkL = setInterval(() => {
          if (window.L) {
            clearInterval(checkL);
            initializeMap();
          }
        }, 100);
        return () => clearInterval(checkL);
      }
    } else {
      initializeMap();
    }

    function initializeMap() {
      if (!window.L || !mapRef.current) return;
      
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
      }

      const map = window.L.map(mapRef.current).setView([mapCenter.lat, mapCenter.lng], mapZoom);

      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19,
      }).addTo(map);

      mapInstanceRef.current = map;
      setTimeout(() => map.invalidateSize(), 80);

      // Update markers when shops change
      if (filteredShops.length > 0) {
        updateMarkers(map);
      }
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [loading, mapCenter, mapZoom]);

  // Update map center and zoom
  useEffect(() => {
    if (!mapInstanceRef.current || shops.length === 0) return;

    const shopsWithCoords = shops
      .map(s => s.coordinates)
      .filter(c => c && isValidEgyptCoordinates(c.latitude, c.longitude));
    
    const allCoords = userLocation && isValidEgyptCoordinates(userLocation.latitude, userLocation.longitude)
      ? [...shopsWithCoords, userLocation]
      : shopsWithCoords;

    if (allCoords.length === 0) {
      setMapCenter({ lat: 30.0444, lng: 31.2357 });
      setMapZoom(11);
      return;
    }

    const latitudes = allCoords.map(c => c.latitude);
    const longitudes = allCoords.map(c => c.longitude);

    const minLat = Math.min(...latitudes);
    const maxLat = Math.max(...latitudes);
    const minLng = Math.min(...longitudes);
    const maxLng = Math.max(...longitudes);

    const centerLat = (minLat + maxLat) / 2;
    const centerLng = (minLng + maxLng) / 2;
    
    const latDiff = Math.max(maxLat - minLat, 0.05);
    const lngDiff = Math.max(maxLng - minLng, 0.05);
    const maxDiff = Math.max(latDiff, lngDiff);
    
    let zoom = 11;
    if (maxDiff > 1) zoom = 9;
    else if (maxDiff > 0.5) zoom = 10;
    else if (maxDiff > 0.2) zoom = 11;
    else if (maxDiff > 0.1) zoom = 12;
    else if (maxDiff > 0.05) zoom = 13;
    else zoom = 14;

    setMapCenter({ lat: centerLat, lng: centerLng });
    setMapZoom(zoom);

    if (mapInstanceRef.current) {
      mapInstanceRef.current.setView([centerLat, centerLng], zoom);
    }
  }, [shops, userLocation]);

  // Filter shops
  const filteredShops = useMemo(() => {
    let filtered = shops;
    
    if (selectedCategory) {
      filtered = filtered.filter(shop => shop.serviceCategory === selectedCategory);
    }
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(shop => {
        return (
          shop.name?.toLowerCase().includes(query) ||
          shop.town?.toLowerCase().includes(query) ||
          shop.city?.toLowerCase().includes(query) ||
          shop.serviceCategory?.toLowerCase().includes(query)
        );
      });
    }
    
    return filtered;
  }, [shops, searchQuery, selectedCategory]);

  // Update markers on map
  const updateMarkers = (map) => {
    if (!window.L || !map) return;
    
    // Clear existing markers
    markersRef.current.forEach(marker => {
      if (marker && marker.remove) {
        marker.remove();
      }
    });
    markersRef.current = [];

    filteredShops.forEach(shop => {
      if (!shop.coordinates) return;

      const { latitude, longitude } = shop.coordinates;
      if (!isValidEgyptCoordinates(latitude, longitude)) return;

      const rating = shop.rating || shop.averageRating || 4.7;
      const rawImageUrl = shop.imageUrl || shop.image || '/images/shop-placeholder.png';
      const imageUrl = imageHandler.getUrl(rawImageUrl);
      const placeholderUrl = '/images/shop-placeholder.png';

      // Create custom marker icon with error handling
      const customIcon = window.L.divIcon({
        className: 'custom-marker',
        html: `
          <div class="marker-container">
            <img 
              src="${imageUrl}" 
              alt="${shop.name || 'Shop'}" 
              class="marker-image"
              onerror="this.onerror=null; this.src='${placeholderUrl}';"
            />
            <div class="marker-rating">${rating.toFixed(1)}</div>
          </div>
        `,
        iconSize: [60, 60],
        iconAnchor: [30, 60],
        popupAnchor: [0, -60],
      });

      const marker = window.L.marker([latitude, longitude], { icon: customIcon })
        .addTo(map)
        .bindPopup(`
          <div class="map-popup">
            <h4>${shop.name || 'Shop'}</h4>
            <p>${shop.town || shop.city || 'Location'}</p>
            <p>⭐ ${rating.toFixed(1)}</p>
          </div>
        `)
        .on('click', () => {
          handleMarkerPress(shop);
        });

      markersRef.current.push(marker);
    });

    // Add user location marker if available
    if (userLocation && isValidEgyptCoordinates(userLocation.latitude, userLocation.longitude)) {
      const userIcon = window.L.divIcon({
        className: 'user-location-marker',
        html: '<div class="user-marker-dot"></div>',
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      });

      const userMarker = window.L.marker([userLocation.latitude, userLocation.longitude], { icon: userIcon })
        .addTo(map);
      markersRef.current.push(userMarker);
    }
  };

  // Update markers when filtered shops change
  useEffect(() => {
    if (mapInstanceRef.current && window.L) {
      updateMarkers(mapInstanceRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredShops, userLocation]);

  // Get shop rating
  const getRating = (shop) => {
    return shop.rating || shop.averageRating || 4.7;
  };

  const getShopGallery = (shop) => {
    if (!shop) return ['/images/shop-placeholder.png'];
    const raw = [shop.imageUrl || shop.image, ...(Array.isArray(shop.gallery) ? shop.gallery : [])].filter(Boolean);
    const urls = [...new Set(raw.map((u) => imageHandler.getUrl(u)).filter(Boolean))];
    return urls.length ? urls : ['/images/shop-placeholder.png'];
  };

  const selectedGallery = useMemo(
    () => getShopGallery(selectedShop),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedShop]
  );

  useEffect(() => {
    setShopImageIndex(0);
    if (galleryRef.current) galleryRef.current.scrollLeft = 0;
  }, [selectedShop]);

  const handleMarkerPress = (shop) => {
    setSelectedShop(shop);
    if (shop.coordinates && mapInstanceRef.current) {
      mapInstanceRef.current.setView([shop.coordinates.latitude, shop.coordinates.longitude], 15);
    }
  };

  // Center on user location
  const centerOnUserLocation = () => {
    if (userLocation && mapInstanceRef.current) {
      mapInstanceRef.current.setView([userLocation.latitude, userLocation.longitude], 15);
    }
  };

  // Zoom controls
  const handleZoomIn = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.zoomIn();
      setMapZoom(mapInstanceRef.current.getZoom());
    }
  };

  const handleZoomOut = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.zoomOut();
      setMapZoom(mapInstanceRef.current.getZoom());
    }
  };

  // Get distance for selected shop
  const getDistance = (shop) => {
    if (!userLocation || !shop.coordinates) return null;
    const distance = calculateDistance(
      userLocation.latitude,
      userLocation.longitude,
      shop.coordinates.latitude,
      shop.coordinates.longitude
    );
    return distance.toFixed(2);
  };

  // Handle category selection
  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setShowCategoryModal(false);
  };

  if (loading) {
    return (
      <div className="map-container">
        <div className="map-loading">
          <p>{lang === 'ar' ? 'جاري تحميل الخريطة...' : 'Loading map...'}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="map-container">
        <div className="map-error">
          <h2>{lang === 'ar' ? 'حدث خطأ' : 'Error'}</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>
            {lang === 'ar' ? 'إعادة المحاولة' : 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`map-screen ${lang === 'ar' ? 'rtl' : ''}`} data-lang={lang}>
      <div className={`map-top-bar ${lang === 'ar' ? 'rtl' : ''}`}>
        <button
          className="map-back-button"
          onClick={() => navigate(-1)}
          aria-label={lang === 'ar' ? 'رجوع' : 'Go back'}
        >
          <ELogo size="small" className="map-top-logo" />
        </button>

        <div className="map-search-input-container">
          <FaSearch className="map-search-icon" size={18} />
          <input
            type="text"
            className={`map-search-input ${lang === 'ar' ? 'map-search-input-rtl' : ''}`}
            dir={lang === 'ar' ? 'rtl' : 'ltr'}
            style={lang === 'ar' ? { textAlign: 'right' } : undefined}
            placeholder={lang === 'ar' ? 'ابحث عن متجر...' : 'Search for a shop...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery.length > 0 && (
            <button
              className="map-clear-button"
              onClick={() => setSearchQuery('')}
              aria-label={lang === 'ar' ? 'مسح البحث' : 'Clear search'}
            >
              <FaTimes size={16} />
            </button>
          )}
        </div>

        <button
          className={`map-filter-button ${selectedCategory ? 'active' : ''}`}
          onClick={() => setShowCategoryModal(true)}
          aria-label={lang === 'ar' ? 'تصفية حسب الفئة' : 'Filter by category'}
        >
          <FaFilter size={18} />
        </button>
      </div>

      {/* Map */}
      <div ref={mapRef} className="map-wrapper" />

      {/* Location Button */}
      {userLocation && (
        <button
          className="map-location-button"
          onClick={centerOnUserLocation}
          aria-label={lang === 'ar' ? 'تحديد موقعي' : 'Center on my location'}
        >
          <FaLocationArrow size={20} />
        </button>
      )}

      {!selectedShop && (
        <div className="map-zoom-controls">
        <button
          className="map-zoom-button"
          onClick={handleZoomIn}
          aria-label={lang === 'ar' ? 'تكبير' : 'Zoom in'}
        >
          <FaPlus size={18} />
        </button>
        <button
          className="map-zoom-button"
          onClick={handleZoomOut}
          aria-label={lang === 'ar' ? 'تصغير' : 'Zoom out'}
        >
          <FaMinus size={18} />
        </button>
      </div>
      )}

      {/* Category Filter Modal */}
      {showCategoryModal && (
        <div className="map-modal-overlay" onClick={() => setShowCategoryModal(false)}>
          <div className="map-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="map-modal-header">
              <h3>{lang === 'ar' ? 'اختر الفئة' : 'Select Category'}</h3>
              <button
                className="map-modal-close"
                onClick={() => setShowCategoryModal(false)}
                aria-label={lang === 'ar' ? 'إغلاق' : 'Close'}
              >
                <FaTimes size={20} />
              </button>
            </div>
            <div className="map-modal-list">
              {serviceCategories.map((category) => (
                <button
                  key={category.serviceCategory || 'all'}
                  className={`map-category-item ${selectedCategory === category.serviceCategory ? 'active' : ''}`}
                  onClick={() => handleCategorySelect(category.serviceCategory)}
                >
                  <span>{lang === 'ar' ? category.titleAr : category.title}</span>
                  {selectedCategory === category.serviceCategory && (
                    <span className="checkmark">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Shop Detail Card */}
      {selectedShop && (
        <div className="map-shop-card">
          <button
            className="map-shop-card-close"
            onClick={() => setSelectedShop(null)}
            aria-label={lang === 'ar' ? 'إغلاق' : 'Close'}
          >
            <FaTimes size={12} />
          </button>

          <div className="map-shop-card-content">
            <div className="map-shop-card-gallery-wrap">
              <div
                ref={galleryRef}
                className="map-shop-card-gallery"
                onScroll={(e) => {
                  const el = e.currentTarget;
                  if (!el.clientWidth) return;
                  const idx = Math.round(el.scrollLeft / el.clientWidth);
                  setShopImageIndex(Math.max(0, Math.min(idx, selectedGallery.length - 1)));
                }}
                onPointerDown={(e) => e.stopPropagation()}
              >
                {selectedGallery.map((src, idx) => (
                  <img
                    key={`${src}-${idx}`}
                    src={src}
                    alt={`${selectedShop.name || 'Shop'} ${idx + 1}`}
                    className="map-shop-card-image"
                    draggable="false"
                    onError={(e) => {
                      const target = e.target;
                      if (!target.src.includes('shop-placeholder.png')) {
                        target.src = '/images/shop-placeholder.png';
                      }
                      target.onerror = null;
                    }}
                  />
                ))}
              </div>
              {selectedGallery.length > 1 ? (
                <div className="map-shop-card-dots">
                  {selectedGallery.map((src, idx) => (
                    <button
                      key={`${src}-dot-${idx}`}
                      type="button"
                      className={`map-shop-card-dot${idx === shopImageIndex ? ' active' : ''}`}
                      aria-label={lang === 'ar' ? `صورة ${idx + 1}` : `Image ${idx + 1}`}
                      onClick={() => {
                        const el = galleryRef.current;
                        if (!el) return;
                        el.scrollTo({ left: idx * el.clientWidth, behavior: 'smooth' });
                        setShopImageIndex(idx);
                      }}
                    />
                  ))}
                </div>
              ) : null}
            </div>

            <div className="map-shop-card-info">
              <h3 className="map-shop-card-name">{selectedShop.name || 'Shop Name'}</h3>

              <div className="map-shop-card-location">
                <FaMapMarkerAlt size={14} />
                <span>
                  {selectedShop.town || selectedShop.city || 'Location'}
                  {getDistance(selectedShop) && ` • ${getDistance(selectedShop)} KM`}
                </span>
              </div>

              <div className="map-shop-card-rating">
                <FaStar size={16} color="#fbbf24" />
                <span>{getRating(selectedShop).toFixed(1)}</span>
              </div>

              <button
                className="map-shop-card-book-button"
                onClick={() => {
                  navigate(`/booking/${selectedShop.name || selectedShop.slug}`);
                }}
              >
                {lang === 'ar' ? 'احجز الآن' : 'Book Now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
